import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { AlertOctagon, AlertTriangle, CalendarIcon, CheckCircle2, Link2 } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { trendDomain } from "../../../components/portfolio-risk-concentration/chartScale";
import { JointMembersTable } from "../../../components/portfolio-risk-concentration/JointMembersTable";
import { PageHeader } from "../../../components/portfolio-risk-concentration/PageHeader";
import { StatCard } from "../../../components/portfolio-risk-concentration/StatCard";
import {
  StatusBadge,
  statusClass,
} from "../../../components/portfolio-risk-concentration/StatusBadge";
import { StepShell } from "../../../components/portfolio-risk-concentration/StepShell";
import {
  directionLabel,
  isBreached,
  limitNames,
  pct,
  pctDistance,
  severityRank,
  thr,
  usd,
  usdM,
  usdMSigned,
} from "../../../components/portfolio-risk-concentration/format";
import { Alert, AlertDescription, AlertTitle } from "../../../ui/alert";
import { Button } from "../../../ui/button";
import { Calendar } from "../../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../../../ui/table";

import {
  INVESTOR_LABEL,
  PrcFilterBar,
  SCOPE_LABEL,
  useFacilityOptions,
  FilterSelect,
  VALUE_BASIS_FILTERS,
  useInvestorFilterOptions,
  usePrcFilters,
  type Investor,
} from "../../../components/portfolio-risk-concentration/PrcFilterBar";
import { useBookLabels } from "../../../utils/portfolio-risk-concentration/api/facilities";
import {
  SortableTableHead,
  directionSortingFn,
  statusSortingFn,
  trendDelta,
  trendSortingFn,
} from "../../../components/portfolio-risk-concentration/SortableTableHead";
import { useCapacity } from "../../../utils/portfolio-risk-concentration/api/capacity";
import { VALUE_BASIS_LABEL } from "../../../utils/portfolio-risk-concentration/api/concentration";
import { useCardCalculations } from "../../../utils/portfolio-risk-concentration/api/card-calculations";
import { CardCalculationCards } from "../../../components/portfolio-risk-concentration/CardCalculationCards";
import {
  useFacilityCapacity,
  type CapacityFacility,
} from "../../../utils/portfolio-risk-concentration/api/facility-capacity";
import { CapacityCards } from "../../../components/portfolio-risk-concentration/CapacityCards";
import type { ConcentrationRow } from "../../../utils/portfolio-risk-concentration/api/types";
import { cn } from "../../../utils/common/cn";

// severityRank / sortBySeverity / pct / usd / usdM / DIRECTION_LABEL / statusClass /
// StatusBadge now live in components/portfolio-risk-concentration (format.ts and
// StatusBadge.tsx) — they were duplicated verbatim here and in MonitorPage.

/**
 * ── There is no longer a "Headroom method" control ────────────────────────────────────────
 * This page used to carry a two-option select — percentage-only vs percentage-or-dollar-cap —
 * which was never a filter and never reached the API: it re-derived RAG, headroom, the
 * sparkline reference line and the row order entirely in the browser. It is gone, and the
 * page now applies **percentage-or-cap unconditionally**, which was already its default and
 * is the stricter of the two (it applies both constraints; the other ignores one).
 *
 * Removing it changes no number on screen today. Every internal `capital_cap_usd` is exactly
 * `threshold_pct x total_capital_usd`, so the cap can never bind before the percentage does,
 * and external limits carry no dollar cap at all — so the two methods were numerically
 * identical on all live data. What it removes is a control that looked like the
 * Internal/External scope filter beside it and got read as one.
 *
 * The scope filter now in its place is the real thing: /capacity takes the same four
 * `facility` values as /concentration.
 */

/** Which of the two constraints is closer to binding, expressed in $ headroom.
 *
 *  `dollars` is nullable because an UNMEASURED row has no headroom to compare — neither
 *  constraint can be evaluated, so the answer is "percentage constraint, amount unknown"
 *  rather than a number. Every consumer renders it through usdM/usdMSigned, which print an
 *  em dash for null. */
function binding(row: ConcentrationRow): { kind: "cap" | "pct"; dollars: number | null } {
  // NULL for every external row — external limits are percentage-only — so external
  // always reports the percentage constraint, which is correct rather than a fallback.
  const capHeadroom = row.capHeadroomDollars;
  if (capHeadroom === null) return { kind: "pct", dollars: row.headroomDollars };
  return capHeadroom <= row.headroomDollars
    ? { kind: "cap", dollars: capHeadroom }
    : { kind: "pct", dollars: row.headroomDollars };
}

/** The % of the row's base at which the more-binding constraint breaches. */
function bindingBreachPct(row: ConcentrationRow): number {
  // A joint's series is now its governing member's own percentage against that member's own
  // threshold, so the reference line is that threshold -- the same rule as a single limit.
  // It used to be a flat 100 because the series was a utilisation.
  if (row.isJoint) return row.thresholdPct || Infinity;
  // denominatorDollars is the row's OWN base as the API scored it — total_capital_usd for
  // internal, the book's total net investment for external — not a constant compiled into
  // the bundle. It used to read a hardcoded 2_500_000_000 from capacity.ts, which silently
  // stopped being true the moment capital planning changed the figure in the warehouse.
  const capitalBase = row.denominatorDollars;
  const capPct =
    row.capitalCapDollars === null || !capitalBase
      ? Infinity
      : (row.capitalCapDollars / capitalBase) * 100;
  // `|| Infinity` guards a zero threshold: without it the ReferenceLine would be drawn on
  // the baseline and read as a breach at zero.
  return Math.min(row.thresholdPct || Infinity, capPct);
}

/** `status` is passed in rather than read off the row: it must be the cap-adjusted one, or
 *  the sparkline colours a row green while the badge beside it reads RED. */
function Sparkline({
  row,
  status,
}: {
  row: ConcentrationRow;
  status: ConcentrationRow["status"];
}) {
  const data = row.trend.map((p) => ({ x: p.date, y: p.dollarPct }));
  // See MonitorPage's Sparkline: an axis with no series reads as "flat at zero".
  if (data.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const breach = bindingBreachPct(row);
  const color =
    status === "RED"
      ? "hsl(var(--destructive))"
      : status === "AMBER"
        ? "var(--amber)"
        : "var(--approve)";
  return (
    <div className="h-9 w-28">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, bottom: 4, left: 0, right: 0 }}>
          {/* Same rule as the detail chart, and it also fixes the Infinity `breach` a row
              with neither a percentage threshold nor a capital cap produces. */}
          <YAxis hide domain={trendDomain(breach)} />
          <ReferenceLine y={breach} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
          <Line type="monotone" dataKey="y" stroke={color} strokeWidth={1.5} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * What the row shows: its status, its binding headroom, and whether the dollar cap is what
 * binds. `row.status` is the API's own percentage-or-cap status and is used as-is.
 *
 * A joint is governed by its lowest-threshold member, and every figure on the row — status,
 * threshold, percentage, headroom — comes from that one member, computed in the backend and
 * deliberately never re-derived here. The cap-vs-percentage comparison is still skipped for
 * joints: the question the row answers is "which member governs", not "which constraint
 * binds", and capHeadroomDollars is the governing member's own.
 */
function display(row: ConcentrationRow) {
  if (row.isJoint) {
    return { status: row.status, headroom: row.headroomDollars, capActive: false };
  }
  const capBinding =
    row.capHeadroomDollars !== null && row.capHeadroomDollars < row.headroomDollars;
  return {
    status: row.status,
    headroom: capBinding ? row.capHeadroomDollars! : row.headroomDollars,
    capActive: capBinding,
  };
}

/** Worst first, then closest to its own threshold. Same ordering as the shared
 *  sortBySeverity, kept local because it ties the order to `display()`'s status so the row
 *  order and the badges beside it cannot disagree. */
function sortForDisplay(rows: ConcentrationRow[]): ConcentrationRow[] {
  return [...rows].sort((a, b) => {
    const r = severityRank(display(a).status) - severityRank(display(b).status);
    if (r !== 0) return r;
    return pctDistance(a) - pctDistance(b);
  });
}

export default function CapacityPage() {
  const bookLabel = useBookLabels();
  const { investorsWithAll } = useInvestorFilterOptions();
  const [searchParams, setSearchParams] = useSearchParams();
  const limitParam = searchParams.get("limit") ?? undefined;

  // The shared filter state — scope, book and investor — read and written exactly as every
  // other page does it, so walking Monitor -> Capacity keeps the whole selection and a scope
  // toggle no longer discards the book.
  const [openId, setOpenId] = useState<string | null>(null);
  const filters = usePrcFilters(searchParams, setSearchParams, () => setOpenId(null));
  const { scope, facility, product, investor, isExternal } = filters;
  const facilityOptions = useFacilityOptions(investor);

  const [asOf, setAsOf] = useState<Date>(() => new Date());
  const asOfDate = format(asOf, "yyyy-MM-dd");

  // include_joints=1: joint rows arrive in the same ConcentrationRow shape, so they slot
  // into the table below without a new UI concept.
  const { data } = useCapacity(
    filters.concentrationFacility, asOfDate, true, investor, facility, product,
    filters.valueBasis,
  );
  // NOT pre-sorted — see MonitorPage. sortForDisplay is kept for the banner below, which
  // still names the tightest limits first however the table happens to be sorted.
  const rows = useMemo(() => data?.rows ?? [], [data]);

  // The real base, off the rows the page is already showing, rather than the TOTAL_CAPITAL
  // constant this page used to print. Every internal row shares one denominator
  // (total_capital_usd), so the first row that carries one is it. Externally the denominator
  // is per-book, so this is only meaningful when one book is selected — which is the same
  // condition the capacity bar below is gated on.
  const capitalBase = useMemo(
    () => rows.find((r) => r.denominatorDollars)?.denominatorDollars ?? null,
    [rows],
  );
  // EXTERNAL: the headline cards read athena_bfsi_prc.card_calculations (sql/68_) -- totals and ceilings per
  // facility x investor x product, computed once on the server with fixed bases (net investment for the portfolio,
  // DCB for every outstanding figure), so the "Measured in" dropdown does not move them. Shown for every investor: one with no
  // rows (Navitas funds through equity) gets the same cards at $0 and 0.0%.
  const showExternalCards = isExternal;
  const { data: cardCalc } = useCardCalculations(facility, asOfDate, investor, product, showExternalCards);
  // INTERNAL: the appetite ceiling (prc_capital_source 'Internal') is not in card_calculations, so the
  // Internal scope keeps its own cards.
  const capacityFacility: CapacityFacility | null = isExternal ? null : "INTERNAL";
  const { data: facilityCapacity } = useFacilityCapacity(
    capacityFacility,
    asOfDate,
    true,
    investor,
    product,
  );

  useEffect(() => {
    if (limitParam) setOpenId(limitParam);
  }, [limitParam]);

  const selected = rows.find((r) => r.limitId === openId) ?? null;

  const columns = useMemo<ColumnDef<ConcentrationRow>[]>(
    () => [
      {
        accessorKey: "limitDesc",
        header: "Limit",
        cell: ({ row }) => (
          <span className="flex items-start gap-1.5 font-medium">
            {row.original.isJoint && (
              <Link2 className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
            )}
            <span>
              {row.original.limitDesc}
              {row.original.isJoint && (
                <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                  · {row.original.memberCount ?? row.original.memberRefs?.length ?? 0} limits
                </span>
              )}
            </span>
          </span>
        ),
      },
      // Only on the External scope, where CAP 1 and CAP 2 rows sit in one table — same column
      // as Monitor's. Each is measured against its OWN book's total net investment, so without
      // it two rows with the same limit description and different percentages look like a bug.
      ...(isExternal
        ? [
            {
              id: "book",
              header: "Book",
              accessorFn: (r) => bookLabel[r.facilityCode] ?? r.facilityCode,
              cell: ({ row }) => (
                <span className="whitespace-nowrap text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  {bookLabel[row.original.facilityCode] ?? row.original.facilityCode}
                </span>
              ),
            } as ColumnDef<ConcentrationRow>,
          ]
        : []),
      // Only under "All investors", where the table holds two investors' limits scored
      // against different capital bases. Naming the frame is what keeps the Current column
      // honest — without it, 0.63% of Wafra's $2.5B sorts against 4.10% of Navitas' $385M
      // as though they were the same measurement.
      ...(investor === "ALL"
        ? [
            {
              id: "investor",
              accessorFn: (r: ConcentrationRow) => r.investor ?? "",
              header: "Investor",
              cell: ({ row }: { row: { original: ConcentrationRow } }) => (
                <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  {INVESTOR_LABEL[(row.original.investor ?? "") as Investor] ??
                    row.original.investor ??
                    "—"}
                </span>
              ),
            } as ColumnDef<ConcentrationRow>,
          ]
        : []),
      {
        accessorKey: "categoryGroup",
        header: "Category",
        cell: ({ row }) => (
          <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
            {row.original.categoryGroup}
          </span>
        ),
      },
      {
        accessorKey: "currentDollarPct",
        // Externally the denominator is the book's total net investment, not a capital base,
        // so the header has to say which — the percentages are not comparable across scopes.
        header: isExternal ? "% of book" : "% of capital",
        cell: ({ row }) => (
          <span
            className="tabular-nums"
            title={row.original.isJoint ? `binding: ${row.original.bindingLimitDesc}` : undefined}
          >
            <span className={cn("font-medium", statusClass(display(row.original).status))}>
              {pct(row.original.currentDollarPct)}
            </span>
            <span className="text-muted-foreground"> / {thr(row.original.thresholdPct)}</span>
            {/* Bare, matching Monitor: the governing member's percentage and threshold
                read like a single limit's. The member breakdown is in the detail sheet. */}
          </span>
        ),
      },
      {
        id: "deployed",
        // Names the BASIS, not just the quantity. With two bases live, a column headed
        // "Net investment" over present-value dollars is the whole failure mode of this
        // feature -- the figures look entirely plausible and nothing says otherwise.
        // Internally the column is what has been DEPLOYED, so it keeps its own wording and
        // takes the basis as a qualifier.
        header: isExternal
          ? VALUE_BASIS_LABEL[filters.valueBasis]
          : filters.valueBasis === "present_value"
            ? "Capital deployed (PV)"
            : "Capital deployed",
        accessorFn: (r) => r.currentDollars,
        cell: ({ row }) => (
          <span className="tabular-nums">
            <span className={cn("font-medium", statusClass(display(row.original).status))}>
              {usdM(row.original.currentDollars)}
            </span>
            {/* External rows have no capital_cap_usd at all, so this reads as a bare figure
                there rather than "x / —". The page caption says why. */}
            {row.original.capitalCapDollars !== null && (
              <span className="text-muted-foreground">
                {" / "}
                {usdM(row.original.capitalCapDollars)}
              </span>
            )}
          </span>
        ),
      },
      {
        id: "headroom",
        header: "Headroom",
        // The BINDING headroom the cell shows, not the raw percentage one — sorting on a
        // number the reader cannot see would look broken on the rows where a cap binds.
        accessorFn: (r) => display(r).headroom,
        cell: ({ row }) => {
          const d = display(row.original);
          return (
            <span
              className={cn(
                "tabular-nums font-medium",
                // The row's RAG, like every other figure in the row — this used to be
                // coloured only when negative, so an AMBER row showed an amber percentage
                // and an amber dollar figure beside a plain white headroom, and a RED row
                // with positive headroom looked half-fine. One row, one colour.
                //
                // Negative still forces destructive: for a single limit a negative headroom
                // implies RED anyway, but a JOINT takes headroom from its smallest member and
                // status from its worst, and those are usually different limits — so a joint
                // can carry a negative headroom while its status is AMBER. Showing that
                // number in amber would understate a breach.
                d.headroom < 0 ? "text-destructive" : statusClass(d.status),
              )}
            >
              {usdMSigned(d.headroom)}
              {/* "governing" / "cap" say which limit the figure comes from; the plain percentage case needs no tag */}
              {row.original.isJoint || d.capActive ? (
                <span className="ml-1 text-xs text-muted-foreground">({row.original.isJoint ? "governing" : "cap"})</span>
              ) : null}
            </span>
          );
        },
      },
      {
        id: "trend",
        header: "Trend",
        accessorFn: trendDelta,
        cell: ({ row }) => (
          <Sparkline row={row.original} status={display(row.original).status} />
        ),
        sortingFn: trendSortingFn,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge
            status={display(row.original).status}
            title={row.original.unmeasuredReason}
          />
        ),
        sortingFn: statusSortingFn,
      },
      {
        accessorKey: "direction",
        header: "Direction",
        cell: ({ row }) => (
          <span
            className={cn(
              "text-xs",
              isBreached(row.original)
                ? "font-medium text-destructive"
                : "text-muted-foreground",
            )}
          >
            {directionLabel(row.original)}
          </span>
        ),
        sortingFn: directionSortingFn,
      },
    ],
    // investor is a dep because the Investor column appears only under "All"; isExternal,
    // because the Book column appears only on External.
    //
    // filters.valueBasis is a dep because the "deployed" column's HEADER is derived
    // from it. Without it the memo never recomputed on a basis change: the rows
    // refetched and the dollars switched to present value, while the header stayed on
    // whatever the basis was at first render -- a column reading "Net investment" over
    // present-value figures, which is precisely the failure this column's own comment
    // warns about. Nothing looked broken; the numbers were just silently mislabelled.
    [isExternal, investor, filters.valueBasis, bookLabel],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Category first, matching Monitor. Every header re-sorts from here.
    initialState: {
      sorting: [
        { id: "categoryGroup", desc: false },
        { id: "limitDesc", desc: false },
      ],
    },
  });

  // Ordered for the banner, not for the table: tightest first, whatever the table shows.
  //
  // Two sets, and the banner must not mix them: `redRows` is what the RED banner counts AND
  // names, `flaggedRows` is every non-GREEN row and belongs to the AMBER one. The red banner
  // used to count reds and then list every non-GREEN limit, so "4 external limit(s) at or
  // near capacity" sat above a list of seven.
  const shownRows = sortForDisplay(rows).map((r) => ({ row: r, d: display(r) }));
  const redRows = shownRows.filter((x) => x.d.status === "RED").map((x) => x.row);
  const redCount = redRows.length;
  const anyRed = redCount > 0;
  const flaggedRows = shownRows.filter((x) => x.d.status !== "GREEN").map((x) => x.row);

  const selectedBinding = selected ? binding(selected) : null;
  const selectedDisplay = selected ? display(selected) : null;
  const scopeWord = isExternal ? "external" : "internal";

  return (
    <StepShell step={2}>
      {/* No "Add limit" action here. Capacity answers "how much room is there", which is a
          reading question; creating a limit is Manage limits' job and it sits one click away
          in the breadcrumb. The button also deep-linked with `facility=` alone, which opens
          the Add form on arrival — so a page for looking at capacity could put you in a
          half-filled creation form. */}
      <PageHeader title="Capacity" />

      {/* The same bar, in the same order, as Monitor / Snapshot / Manage limits. */}
      <div className="mt-6">
        <PrcFilterBar
          scope={scope}
          onScopeChange={filters.setScope}
          facility={facility}
          onFacilityChange={filters.setFacility}
          product={product}
          onProductChange={filters.setProduct}
          investor={investor}
          onInvestorChange={filters.setInvestor}
          investorOptions={investorsWithAll}
          children={
            <FilterSelect
              label="Measured in"
              value={filters.valueBasis}
              options={VALUE_BASIS_FILTERS}
              onChange={filters.setValueBasis}
            />
          }
          right={
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 gap-2">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  As of <span suppressHydrationWarning>{format(asOf, "d MMM yyyy")}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={asOf}
                  onSelect={(d) => d && setAsOf(d)}
                  defaultMonth={asOf}
                  captionLayout="dropdown-buttons"
                  fromYear={2015}
                  toYear={new Date().getFullYear()}
                  disabled={{ after: new Date() }}
                />
              </PopoverContent>
            </Popover>
          }
        />
      </div>

      {/* The ceiling and what is drawn against it. External: card_calculations, per facility x investor x
          product. Internal: the appetite ceiling, from /facility-capacity. */}
      {showExternalCards && cardCalc && (
        <CardCalculationCards
          data={cardCalc}
          facilityLabel={facilityOptions.find((f) => f.value === facility)?.label}
          className="mt-5"
        />
      )}
      {facilityCapacity && (
        <CapacityCards
          data={facilityCapacity}
          scope={scope}
          facility={facility}
          investor={investor}
          className="mt-5"
        />
      )}

      {/* Same as Monitor: an empty selection is answered, not silently rendered as a
          clean bill of health. */}
      {data && rows.length === 0 && (
        <Alert className="mt-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No limits in this selection</AlertTitle>
          <AlertDescription>
            {data.notes?.[0] ??
              "Nothing matches the current filters. Try a different scope or facility."}
          </AlertDescription>
        </Alert>
      )}

      {data && rows.length > 0 && (
        <Alert
          className={cn(
            "mt-4",
            anyRed
              ? "border-destructive/50 bg-destructive/10"
              : flaggedRows.length >= 3
                ? "border-amber/50 bg-amber-dim/40"
                : "border-[color:var(--approve)]/40 bg-[color:var(--approve)]/10",
          )}
        >
          {anyRed ? (
            <AlertOctagon className="h-4 w-4 text-destructive" />
          ) : flaggedRows.length >= 3 ? (
            <AlertTriangle className="h-4 w-4 text-amber" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-[color:var(--approve)]" />
          )}

          <AlertTitle>
            {anyRed
              ? `${redCount} ${scopeWord} limit(s) at or near capacity`
              : flaggedRows.length >= 3
                ? `${flaggedRows.length} ${scopeWord} limits approaching capacity`
                : `Room to deploy against every ${scopeWord} limit`}
          </AlertTitle>

          <AlertDescription>
            {/* Named from the SAME set the title counted — see redRows above. */}
            {anyRed
              ? `${SCOPE_LABEL[scope]} appetite as of ${format(asOf, "d MMM yyyy")}: ${limitNames(redRows, bookLabel)}.`
              : flaggedRows.length > 0
                ? `${SCOPE_LABEL[scope]} appetite as of ${format(asOf, "d MMM yyyy")}: ${limitNames(flaggedRows, bookLabel)}.`
                : `${SCOPE_LABEL[scope]} appetite as of ${format(asOf, "d MMM yyyy")}: no limit is within 10% of its threshold.`}
          </AlertDescription>
        </Alert>
      )}

      <div className="mt-4 overflow-hidden rounded-lg border border-border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => (
                  <SortableTableHead key={h.id} header={h} />
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((r) => (
              <TableRow
                key={r.id}
                onClick={() => setOpenId(r.original.limitId)}
                className="cursor-pointer"
              >
                {r.getVisibleCells().map((c) => (
                  <TableCell key={c.id}>
                    {flexRender(c.column.columnDef.cell, c.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* The methodology notes that used to sit here are gone, as on Monitor. `data.notes`
          is still read — the empty-state alert above shows notes[0], which is where the
          "this investor funds no external book" explanation surfaces. */}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
          {selected && selectedBinding && selectedDisplay && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2 pr-6">
                  <span>{selected.limitDesc}</span>
                  <StatusBadge
                    status={selectedDisplay.status}
                    title={selected.unmeasuredReason}
                  />
                </SheetTitle>
                <SheetDescription>
                  {selected.isJoint ? (
                    <>
                      Joint of {selected.memberCount ?? selected.memberRefs?.length ?? 0} limits,
                      governed by{" "}
                      <strong>{selected.governingLimitDesc ?? selected.bindingLimitDesc}</strong> —
                      its lowest threshold, at {thr(selected.thresholdPct)}. Every figure below is that
                      limit's, and the dashed line marks its threshold.
                      {selected.tightestCapacityLimitDesc && (
                        <>
                          {" "}
                          <strong>{selected.tightestCapacityLimitDesc}</strong> has less room still,
                          so it is what actually caps new business here.
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {selected.categoryGroup} — internal appetite. Dashed line marks the{" "}
                      {selectedBinding.kind === "cap" ? "capital cap" : "percentage threshold"}, the
                      binding constraint today.
                    </>
                  )}
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-4 px-4 pb-6">
                {selected.isJoint && <JointMembersTable row={selected} />}
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={selected.trend.map((p) => ({ x: p.date, y: p.dollarPct }))}
                      margin={{ top: 8, right: 8, bottom: 0, left: -12 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="x" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                      {/* Domain, not the default: a bare YAxis scales to the series and
                          clips the threshold line off the top — see trendDomain. */}
                      <YAxis
                        tick={{ fontSize: 10 }}
                        domain={trendDomain(bindingBreachPct(selected))}
                      />
                      <Tooltip
                        formatter={(v: number) => pct(Number(v))}
                        contentStyle={{ backgroundColor: "#fff", border: "1px solid #ccc" }}
                        labelStyle={{ color: "#000" }}
                        itemStyle={{ color: "#000" }}
                      />
                      <ReferenceLine
                        y={bindingBreachPct(selected)}
                        stroke="hsl(var(--destructive))"
                        strokeDasharray="4 4"
                      />
                      <Line
                        type="monotone"
                        dataKey="y"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <StatCard
                    label={isExternal ? "% of book" : "% of capital"}
                    value={`${pct(selected.currentDollarPct)} / ${thr(selected.thresholdPct)}`}
                    sublabel={`${usdM(selected.headroomDollars)} headroom${
                      selectedBinding.kind === "pct" ? " · binding" : ""
                    }`}
                    accent={selectedBinding.kind === "pct" && selectedDisplay.status !== "GREEN"
                      ? selectedDisplay.status === "RED"
                        ? "red"
                        : "amber"
                      : "default"}
                  />
                  {/* Only where a cap exists. External limits have none, and a greyed-out
                      "No cap set" card said nothing the caption does not say better. */}
                  {selected.capitalCapDollars !== null && (
                    <StatCard
                      label="Capital cap"
                      value={`${usdM(selected.currentDollars)} / ${usdM(selected.capitalCapDollars)}`}
                      sublabel={`${
                        selected.capHeadroomDollars === null
                          ? "No cap set"
                          : `${usdM(selected.capHeadroomDollars)} headroom`
                      }${selectedBinding.kind === "cap" ? " · binding" : ""}`}
                      accent={
                        selectedBinding.kind === "cap" && selectedDisplay.status !== "GREEN"
                          ? selectedDisplay.status === "RED"
                            ? "red"
                            : "amber"
                          : "default"
                      }
                    />
                  )}
                </div>

                <p className="text-xs text-muted-foreground">
                  {selected.isJoint
                    ? `Governing member: ${selected.governingLimitDesc ?? selected.bindingLimitDesc} — the lowest threshold of the ${selected.memberCount} limits combined here, with ${usd(selected.headroomDollars)} left to deploy against it.`
                    : `Binding constraint: the ${selectedBinding.kind === "cap" ? "dollar cap" : "percentage threshold"} — ${usd(selectedBinding.dollars)} left to deploy.`}
                </p>

                <Button asChild className="w-full">
                  {selected.isJoint ? (
                    <Link
                      to={`/risk-concentrations/manage-limits?${new URLSearchParams({
                        facility: scope,
                        ...(isExternal && facility !== "ALL" ? { book: facility } : {}),
                        view: "approved",
                        jointId: selected.limitId,
                        from: "capacity",
                      }).toString()}`}
                    >
                      Adjust this joint
                    </Link>
                  ) : (
                    <Link
                      to={`/risk-concentrations/manage-limits?${new URLSearchParams({
                        facility: scope,
                        ...(isExternal && facility !== "ALL" ? { book: facility } : {}),
                        limitId: selected.limitId,
                        from: "capacity",
                      }).toString()}`}
                    >
                      Modify this limit
                    </Link>
                  )}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </StepShell>
  );
}
